import React from "react";

// Dependency-free radar/spider chart drawn as plain SVG.
// Renders cleanly in the browser AND in print/PDF (it is just SVG).
// props:
//   data: [{ label, value }]  value in 0..max
//   max:  axis maximum (default 100)
//   size: square px size (default 360)
//   color: polygon stroke/fill color

const polarToXY = (cx, cy, radius, angleRad) => ({
  x: cx + radius * Math.cos(angleRad),
  y: cy + radius * Math.sin(angleRad),
});

function RadarChart({ data = [], max = 100, size = 360, color = "#5c1090" }) {
  const padding = 56; // room for labels
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size - padding * 2) / 2;
  const n = data.length;
  if (!n) return null;

  // start at top (-90deg), go clockwise
  const angleAt = (i) => (-Math.PI / 2) + (i * 2 * Math.PI) / n;
  const rings = [0.25, 0.5, 0.75, 1];

  const valuePoints = data.map((d, i) => {
    const r = (Math.max(0, Math.min(max, d.value || 0)) / max) * radius;
    return polarToXY(cx, cy, r, angleAt(i));
  });
  const polygon = valuePoints.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* grid rings */}
      {rings.map((ring, ri) => {
        const pts = data
          .map((_, i) => {
            const p = polarToXY(cx, cy, radius * ring, angleAt(i));
            return `${p.x},${p.y}`;
          })
          .join(" ");
        return (
          <polygon
            key={ri}
            points={pts}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        );
      })}

      {/* ring scale labels (0,25,50,75,100) along the top axis */}
      {[0, ...rings].map((ring, i) => (
        <text
          key={`s${i}`}
          x={cx + 4}
          y={cy - radius * ring}
          fontSize={10}
          fill="#9ca3af"
          dominantBaseline="middle"
        >
          {Math.round(max * ring)}
        </text>
      ))}

      {/* spokes */}
      {data.map((_, i) => {
        const p = polarToXY(cx, cy, radius, angleAt(i));
        return (
          <line
            key={`l${i}`}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        );
      })}

      {/* value polygon */}
      <polygon
        points={polygon}
        fill={color}
        fillOpacity={0.18}
        stroke={color}
        strokeWidth={2}
      />
      {valuePoints.map((p, i) => (
        <circle key={`p${i}`} cx={p.x} cy={p.y} r={3} fill={color} />
      ))}

      {/* axis labels */}
      {data.map((d, i) => {
        const p = polarToXY(cx, cy, radius + 18, angleAt(i));
        const anchor =
          Math.abs(p.x - cx) < 1 ? "middle" : p.x > cx ? "start" : "end";
        return (
          <text
            key={`t${i}`}
            x={p.x}
            y={p.y}
            fontSize={11}
            fill="#374151"
            textAnchor={anchor}
            dominantBaseline="middle"
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}

export default RadarChart;
